import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog";
import { Button } from "@/modules/shared/ui/components/button";
import { Input } from "@/modules/shared/ui/components/input";
import { useInitiateTopUp } from "../../hooks/use-wallet";
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations";
import { setPendingPaymentReturn } from "@/lib/payment-return";
import { toast } from "sonner";

interface TopUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TopUpDialog({ open, onOpenChange }: TopUpDialogProps) {
  const [amount, setAmount] = useState("");
  const [locked, setLocked] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("Processing your request");
  const { mutateAsync, isPending } = useInitiateTopUp();
  const org = useCurrentOrganization();

  const showOverlay = locked || isPending;

  const numericAmount = parseFloat(amount) || 0;
  const vatAmount = numericAmount * 0.15;
  const totalAmount = numericAmount + vatAmount;

  const handleSubmit = async () => {
    if (numericAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      setLocked(true);
      const result = await mutateAsync(numericAmount);
      setProcessingMessage("Redirecting to payment gateway");

      // Remember which org we're topping up for and where to return, so the
      // success page can restore org context (Redux is reset by the full-page
      // redirect back from PayFast) and send the user back to this wallet.
      if (org?.id) {
        setPendingPaymentReturn({ organizationId: org.id, returnPath: "/wallet" });
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = result.redirectUrl;

      Object.entries(result.formData).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch {
      toast.error("Failed to initiate top-up. Please try again.");
      setLocked(false);
      setProcessingMessage("Processing your request");
    }
  };

  const presetAmounts = [100, 250, 500, 1000];

  return (
    <Dialog open={open} onOpenChange={showOverlay ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card">
        <div className="relative grid gap-4">
          {showOverlay && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 rounded-lg bg-background/80 backdrop-blur-md">
              <div className="relative h-14 w-14">
                <div className="absolute inset-0 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin [animation-duration:0.9s]" />
                <div className="absolute inset-2 rounded-full border-[3px] border-primary/20 border-b-primary animate-spin [animation-duration:0.6s] [animation-direction:reverse]" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-primary animate-ping" />
                </div>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-sm font-semibold text-foreground">{processingMessage}</p>
                <p className="text-xs text-muted-foreground">Please don't close this window</p>
              </div>
            </div>
          )}

        <DialogHeader>
          <DialogTitle>Top Up Wallet</DialogTitle>
          <DialogDescription>
            Add funds to your organisation wallet to use AI features.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground">
              Amount (ZAR)
            </label>
            <Input
              type="number"
              min="1"
              step="0.01"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1"
              disabled={showOverlay}
            />
          </div>

          <div className="flex gap-2">
            {presetAmounts.map((preset) => (
              <Button
                key={preset}
                variant="outline"
                size="sm"
                onClick={() => setAmount(preset.toString())}
                className="flex-1"
                disabled={showOverlay}
              >
                R{preset}
              </Button>
            ))}
          </div>

          {numericAmount > 0 && (
            <div className="rounded-lg bg-muted p-3 space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Amount</span>
                <span>R{numericAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>VAT (15%)</span>
                <span>R{vatAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-foreground border-t pt-1">
                <span>Total</span>
                <span>R{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={showOverlay}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={showOverlay || numericAmount <= 0}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Pay R{totalAmount.toFixed(2)}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
